import {
  alpha,
  darken,
  getContrastRatio,
  lighten,
} from '@mui/material/styles';

const buildGradient = (start, end) =>
  `linear-gradient(180deg, ${start} 0%, ${end} 100%)`;

const getReadableText = background => {
  const darkText = '#000000';
  const lightText = '#ffffff';

  return getContrastRatio(background, darkText) >= getContrastRatio(background, lightText)
    ? darkText
    : lightText;
};

const buildAccent = main => ({
  contrastText: getReadableText(main),
  dark: darken(main, 0.2),
  light: lighten(main, 0.2),
  main,
});

const createCommunityPalette = ({
  backgroundDefault,
  backgroundPaper,
  divider,
  error,
  gradients,
  icon,
  inactive = icon,
  info,
  mode,
  primary,
  scheduleMain = primary,
  scheduleOverflowNextDay = inactive,
  scheduleOverflowSameDay = info,
  secondary,
  success,
  surface0,
  surface1,
  surface2,
  text,
  textSecondary,
  tooltipBackground,
  warning,
}) => ({
  mode,
  primary: buildAccent(primary),
  secondary: buildAccent(secondary),
  error: buildAccent(error),
  warning: buildAccent(warning),
  success: buildAccent(success),
  info: buildAccent(info),
  text: {
    primary: text,
    secondary: textSecondary,
    link: secondary,
  },
  link: secondary,
  icon,
  background: {
    default: backgroundDefault,
    paper: backgroundPaper,
  },
  divider,
  business: {
    statusRunning: success,
    statusActive: secondary,
    statusInactive: inactive,
    statusWarning: error,
    scheduleMain,
    scheduleOverflowSameDay,
    scheduleOverflowNextDay,
  },
  app: {
    labelDefault: textSecondary,
    metricBackground: surface0,
    popupBorder: surface2,
    schedulePastBackground: surface1,
    scheduleSelectedBackground: alpha(primary, mode === 'dark' ? 0.28 : 0.18),
    scheduleValueBackground: surface0,
    tooltipBackground,
    tooltipBorder: surface1,
  },
  gradients: {
    grey: buildGradient(surface2, icon),
    blue: buildGradient(gradients.sky, secondary),
    indigo: buildGradient(gradients.lavender, primary),
    green: buildGradient(gradients.teal, success),
    orange: buildGradient(gradients.yellow, warning),
    red: buildGradient(gradients.pink, error),
  },
});

// Catppuccin v1.8.0: Latte (light) and Mocha (dark).
// https://catppuccin.com/palette/
const catppuccinLatte = {
  backgroundDefault: '#e6e9ef',
  backgroundPaper: '#eff1f5',
  divider: '#bcc0cc',
  error: '#d20f39',
  gradients: {
    pink: '#ea76cb',
    yellow: '#df8e1d',
    teal: '#179299',
    sky: '#04a5e5',
    lavender: '#7287fd',
  },
  icon: '#7c7f93',
  info: '#04a5e5',
  mode: 'light',
  primary: '#8839ef',
  scheduleMain: '#7287fd',
  scheduleOverflowSameDay: '#179299',
  secondary: '#1e66f5',
  success: '#40a02b',
  surface0: '#ccd0da',
  surface1: '#bcc0cc',
  surface2: '#acb0be',
  text: '#4c4f69',
  textSecondary: '#6c6f85',
  tooltipBackground: '#dce0e8',
  warning: '#fe640b',
};

const catppuccinMocha = {
  backgroundDefault: '#1e1e2e',
  backgroundPaper: '#313244',
  divider: '#45475a',
  error: '#f38ba8',
  gradients: {
    pink: '#f5c2e7',
    yellow: '#f9e2af',
    teal: '#94e2d5',
    sky: '#89dceb',
    lavender: '#b4befe',
  },
  icon: '#9399b2',
  info: '#89dceb',
  mode: 'dark',
  primary: '#cba6f7',
  scheduleMain: '#b4befe',
  scheduleOverflowSameDay: '#94e2d5',
  secondary: '#89b4fa',
  success: '#a6e3a1',
  surface0: '#313244',
  surface1: '#45475a',
  surface2: '#585b70',
  text: '#cdd6f4',
  textSecondary: '#a6adc8',
  tooltipBackground: '#11111b',
  warning: '#fab387',
};

// Ethan Schoonover's Solarized: symmetric Light and Dark palettes.
// https://ethanschoonover.com/solarized/
const solarizedLight = {
  backgroundDefault: '#eee8d5',
  backgroundPaper: '#fdf6e3',
  divider: '#93a1a1',
  error: '#dc322f',
  gradients: {
    pink: '#d33682',
    yellow: '#b58900',
    teal: '#2aa198',
    sky: '#268bd2',
    lavender: '#6c71c4',
  },
  icon: '#839496',
  info: '#2aa198',
  mode: 'light',
  primary: '#6c71c4',
  scheduleOverflowSameDay: '#2aa198',
  secondary: '#268bd2',
  success: '#859900',
  surface0: '#eee8d5',
  surface1: '#93a1a1',
  surface2: '#839496',
  text: '#586e75',
  textSecondary: '#657b83',
  tooltipBackground: '#eee8d5',
  warning: '#cb4b16',
};

const solarizedDark = {
  ...solarizedLight,
  backgroundDefault: '#002b36',
  backgroundPaper: '#073642',
  divider: '#586e75',
  icon: '#657b83',
  mode: 'dark',
  surface0: '#073642',
  surface1: '#586e75',
  surface2: '#657b83',
  text: '#93a1a1',
  textSecondary: '#839496',
  tooltipBackground: '#002b36',
};

// Gruvbox's official light/dark Vim palette.
// https://github.com/morhetz/gruvbox
const gruvboxLight = {
  backgroundDefault: '#ebdbb2',
  backgroundPaper: '#fbf1c7',
  divider: '#bdae93',
  error: '#9d0006',
  gradients: {
    pink: '#8f3f71',
    yellow: '#b57614',
    teal: '#427b58',
    sky: '#076678',
    lavender: '#8f3f71',
  },
  icon: '#665c54',
  info: '#427b58',
  mode: 'light',
  primary: '#af3a03',
  scheduleOverflowSameDay: '#427b58',
  secondary: '#076678',
  success: '#79740e',
  surface0: '#d5c4a1',
  surface1: '#bdae93',
  surface2: '#a89984',
  text: '#282828',
  textSecondary: '#504945',
  tooltipBackground: '#f2e5bc',
  warning: '#b57614',
};

const gruvboxDark = {
  backgroundDefault: '#282828',
  backgroundPaper: '#3c3836',
  divider: '#665c54',
  error: '#fb4934',
  gradients: {
    pink: '#d3869b',
    yellow: '#fabd2f',
    teal: '#8ec07c',
    sky: '#83a598',
    lavender: '#d3869b',
  },
  icon: '#a89984',
  info: '#8ec07c',
  mode: 'dark',
  primary: '#fe8019',
  scheduleOverflowSameDay: '#8ec07c',
  secondary: '#83a598',
  success: '#b8bb26',
  surface0: '#3c3836',
  surface1: '#504945',
  surface2: '#665c54',
  text: '#fbf1c7',
  textSecondary: '#d5c4a1',
  tooltipBackground: '#1d2021',
  warning: '#fabd2f',
};

// Tokyo Night: Day (light) and Night (dark).
// https://github.com/folke/tokyonight.nvim
const tokyoNightDay = {
  backgroundDefault: '#d0d5e3',
  backgroundPaper: '#e1e2e7',
  divider: '#a8aecb',
  error: '#c64343',
  gradients: {
    pink: '#d20065',
    yellow: '#8c6c3e',
    teal: '#118c74',
    sky: '#007197',
    lavender: '#7847bd',
  },
  icon: '#8990b3',
  info: '#007197',
  mode: 'light',
  primary: '#2e7de9',
  scheduleOverflowSameDay: '#118c74',
  secondary: '#9854f1',
  success: '#587539',
  surface0: '#c4c8da',
  surface1: '#b7c1e3',
  surface2: '#a8aecb',
  text: '#3760bf',
  textSecondary: '#6172b0',
  tooltipBackground: '#d0d5e3',
  warning: '#b15c00',
};

const tokyoNightNight = {
  backgroundDefault: '#1a1b26',
  backgroundPaper: '#292e42',
  divider: '#414868',
  error: '#db4b4b',
  gradients: {
    pink: '#ff007c',
    yellow: '#e0af68',
    teal: '#1abc9c',
    sky: '#7dcfff',
    lavender: '#9d7cd8',
  },
  icon: '#737aa2',
  info: '#7dcfff',
  mode: 'dark',
  primary: '#7aa2f7',
  scheduleOverflowSameDay: '#1abc9c',
  secondary: '#bb9af7',
  success: '#9ece6a',
  surface0: '#292e42',
  surface1: '#414868',
  surface2: '#545c7e',
  text: '#c0caf5',
  textSecondary: '#a9b1d6',
  tooltipBackground: '#16161e',
  warning: '#ff9e64',
};

// Rosé Pine v4: Dawn (light) and Moon (dark).
// https://rosepinetheme.com/palette/
const rosePineDawn = {
  backgroundDefault: '#faf4ed',
  backgroundPaper: '#fffaf3',
  divider: '#9893a5',
  error: '#b4637a',
  gradients: {
    pink: '#d7827e',
    yellow: '#ea9d34',
    teal: '#286983',
    sky: '#56949f',
    lavender: '#907aa9',
  },
  icon: '#9893a5',
  info: '#56949f',
  mode: 'light',
  primary: '#907aa9',
  scheduleOverflowSameDay: '#286983',
  secondary: '#56949f',
  success: '#286983',
  surface0: '#f2e9e1',
  surface1: '#e5ddd5',
  surface2: '#cecacd',
  text: '#464261',
  textSecondary: '#797593',
  tooltipBackground: '#f2e9e1',
  warning: '#ea9d34',
};

const rosePineMoon = {
  backgroundDefault: '#232136',
  backgroundPaper: '#2a273f',
  divider: '#393552',
  error: '#eb6f92',
  gradients: {
    pink: '#ea9a97',
    yellow: '#f6c177',
    teal: '#3e8fb0',
    sky: '#9ccfd8',
    lavender: '#c4a7e7',
  },
  icon: '#6e6a86',
  info: '#9ccfd8',
  mode: 'dark',
  primary: '#c4a7e7',
  scheduleOverflowSameDay: '#3e8fb0',
  secondary: '#9ccfd8',
  success: '#3e8fb0',
  surface0: '#2a273f',
  surface1: '#393552',
  surface2: '#6e6a86',
  text: '#e0def4',
  textSecondary: '#908caa',
  tooltipBackground: '#232136',
  warning: '#f6c177',
};

const createPair = (light, dark) => ({
  light: createCommunityPalette(light),
  dark: createCommunityPalette(dark),
});

export const communityPalettes = {
  catppuccin: createPair(catppuccinLatte, catppuccinMocha),
  solarized: createPair(solarizedLight, solarizedDark),
  gruvbox: createPair(gruvboxLight, gruvboxDark),
  tokyoNight: createPair(tokyoNightDay, tokyoNightNight),
  rosePine: createPair(rosePineDawn, rosePineMoon),
};
